// src/contexts/RegistrationProvider.tsx

import { useState, useMemo, useCallback, type ReactNode } from 'react';
import secureLocalStorage from 'react-secure-storage';
import { infoToast } from '../utils/helpers/toasts';

import {
  RegistrationContext,
  type Trade,
  type PromoCode,
  type User,
} from './registrationContext'; // Import the context from the new file

interface RegistrationProviderProps {
  children: ReactNode;
}

export const RegistrationProvider = ({
  children,
}: RegistrationProviderProps) => {
  // r stands for registration and then the next letter is the first letter of the key
  const registrationKeys = {
    trades: 'r.t',
    user: 'r.u',
    serviceAreaIds: 'r.a',
    arrayOfAreaIdWithSpecialtyId: 'r.as',
    promoCodes: 'r.pc',
  };

  const [user, setUser] = useState(() => {
    const storedState = secureLocalStorage.getItem(
      registrationKeys.user
    ) as User;
    return storedState || ({} as User);
  });

  const [promoCodes, setPromoCodes] = useState(() => {
    const storedState = secureLocalStorage.getItem(
      registrationKeys.promoCodes
    ) as PromoCode[];
    return storedState || [];
  });

  const addPromoCode = useCallback(
    (promoCode: PromoCode) => {
      setPromoCodes((prev: PromoCode[]) => {
        const exists = prev.some(
          (currentPromoCode: PromoCode) =>
            currentPromoCode?.promo_code === promoCode?.promo_code ||
            currentPromoCode?.coupon_name === promoCode?.coupon_name
        );

        if (exists) {
          infoToast('Promo code already applied');
          return prev;
        }

        const updatedPromoCodes = [...prev, promoCode].sort((a, b) =>
          a.promo_code.localeCompare(b.promo_code)
        );
        secureLocalStorage.setItem(
          registrationKeys.promoCodes,
          updatedPromoCodes
        );

        return updatedPromoCodes;
      });
    },
    [registrationKeys.promoCodes]
  );

  const removePromoCode = useCallback(
    (promoCodeToBeRemoved: PromoCode) => {
      setPromoCodes((prev: PromoCode[]) => {
        const updatedPromoCodes = prev.filter(
          (promoCode) =>
            promoCode.promo_code !== promoCodeToBeRemoved.promo_code
        );
        secureLocalStorage.setItem(
          registrationKeys.promoCodes,
          updatedPromoCodes
        );
        return updatedPromoCodes;
      });
    },
    [registrationKeys.promoCodes]
  );

  const clearPromoCodes = useCallback(() => {
    setPromoCodes([]);
    secureLocalStorage.removeItem(registrationKeys.promoCodes);
  }, [registrationKeys.promoCodes]);

  const [hasPreviouslyMounted, setHasPreviouslyMounted] = useState(false);

  const [trades, setTrades] = useState(() => {
    const storedState = secureLocalStorage.getItem(
      registrationKeys.trades
    ) as Trade[];

    if (storedState) {
      try {
        return storedState.sort((a, b) =>
          a.full_specialty_name.localeCompare(b.full_specialty_name)
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        secureLocalStorage.removeItem(registrationKeys.trades);
        return [];
      }
    }

    return [];
  });

  const resetRegistrationContext = useCallback(() => {
    setHasPreviouslyMounted(false);
    setUser({} as User);
    setTrades([]);
    setPromoCodes([]);
    secureLocalStorage.clear();
  }, []);

  const resetUser = useCallback(() => {
    setUser({} as User);
    secureLocalStorage.removeItem(registrationKeys.user);
  }, [registrationKeys.user]);

  const updateUserField = useCallback(
    <K extends keyof User>(field: K, value: User[K]) => {
      setHasPreviouslyMounted(true);
      setUser((prev: User) => {
        const updatedUser = {
          ...prev,
          [field]: value,
        };

        secureLocalStorage.setItem(registrationKeys.user, updatedUser);

        return updatedUser as User;
      });
    },
    [registrationKeys.user]
  );

  const updateUser = useCallback(
    (updatedUserFields: Partial<User>) => {
      setUser((prev: User) => {
        const updatedUser = {
          ...prev,
          ...updatedUserFields,
        };

        secureLocalStorage.setItem(registrationKeys.user, updatedUser);

        return updatedUser as User;
      });
    },
    [registrationKeys.user]
  );

  const resetTrades = useCallback(() => {
    setTrades([]);
    secureLocalStorage.removeItem(registrationKeys.trades);
  }, [registrationKeys.trades]);

  const addTrade = useCallback(
    (trade: Trade) => {
      setTrades((prev: Trade[]) => {
        const exists = prev.some(
          (currentTrade) =>
            currentTrade.full_specialty_name === trade.full_specialty_name &&
            currentTrade.industry === trade.industry
        );

        if (exists) {
          return prev;
        }

        const updatedTrades = [...prev, trade].sort((a, b) =>
          a.full_specialty_name.localeCompare(b.full_specialty_name)
        );

        secureLocalStorage.setItem(registrationKeys.trades, updatedTrades);

        return updatedTrades;
      });
    },
    [registrationKeys.trades]
  );

  const addTrades = useCallback(
    (trades: Trade[]) => {
      setTrades((prev: Trade[]) => {
        const uniqueTrades = trades.filter(
          (trade) =>
            !prev.some(
              (currentTrade) =>
                currentTrade.full_specialty_name ===
                  trade.full_specialty_name &&
                currentTrade.industry === trade.industry
            )
        );
        const updatedTrades = [...prev, ...uniqueTrades].sort((a, b) =>
          a.full_specialty_name.localeCompare(b.full_specialty_name)
        );

        secureLocalStorage.setItem(registrationKeys.trades, updatedTrades);

        return updatedTrades;
      });
    },
    [registrationKeys.trades]
  );

  const removeTrade = useCallback(
    (tradeToBeRemoved: Trade) => {
      setTrades((prev: Trade[]) => {
        const updatedTrades = prev
          .filter(
            (trade) =>
              !(
                trade.full_specialty_name ===
                  tradeToBeRemoved.full_specialty_name &&
                trade.industry === tradeToBeRemoved.industry
              )
          )
          .sort((a, b) =>
            a.full_specialty_name.localeCompare(b.full_specialty_name)
          );

        secureLocalStorage.setItem(registrationKeys.trades, updatedTrades);

        return updatedTrades;
      });
    },
    [registrationKeys.trades]
  );

  const value = useMemo(
    () => ({
      resetRegistrationContext,
      hasPreviouslyMounted,
      user,
      resetUser,
      updateUserField,
      updateUser,
      promoCodes,
      addPromoCode,
      removePromoCode,
      clearPromoCodes,
      trades,
      setTrades,
      resetTrades,
      addTrade,
      addTrades,
      removeTrade,
    }),
    [
      resetRegistrationContext,
      hasPreviouslyMounted,
      user,
      resetUser,
      updateUserField,
      updateUser,
      promoCodes,
      addPromoCode,
      removePromoCode,
      clearPromoCodes,
      trades,
      resetTrades,
      addTrade,
      addTrades,
      removeTrade,
    ]
  );

  return (
    <RegistrationContext.Provider value={value}>
      {children}
    </RegistrationContext.Provider>
  );
};
